import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Phone,
  MapPin,
  Clock,
  PartyPopper,
  ArrowRight,
  Mic2,
  UtensilsCrossed,
} from "lucide-react";
import GuestCarousel from "@/components/GuestCarousel";
import {
  WEEKLY_EVENTS,
  CONTACT,
  EVENT_TYPES,
  SIGNATURE_DISHES,
} from "@/data/site";
import useApiCollection from "@/lib/useApiCollection";
import { imageUrl } from "@/lib/api";
import { useEffect, useState } from "react";
import apiClient from "@/lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: "easeOut" },
  }),
};

function useSettings() {
  const [s, setS] = useState({});
  useEffect(() => {
    apiClient.get("/api/settings").then((r) => setS(r.data || {})).catch(() => {});
  }, []);
  return s;
}

export default function Home() {
  const { data: adminWeekly } = useApiCollection("/api/weekly-events");
  const { data: adminDishes } = useApiCollection("/api/signature-dishes");
  const settings = useSettings();

  // Helpers
  const t = (key, fallback) => (settings[key] && settings[key].trim()) || fallback;
  const isHidden = (key) => (settings[key] || "").toLowerCase().trim() === "yes";

  const weeklyEvents = adminWeekly && adminWeekly.length > 0
    ? adminWeekly.map((w) => ({ day: w.day, title: w.title, time: w.time, desc: w.desc, img: imageUrl(w.image_url) }))
    : WEEKLY_EVENTS;
  const signatureDishes = adminDishes && adminDishes.length > 0
    ? adminDishes.map((d) => ({ name: d.name, blurb: d.blurb, img: imageUrl(d.image_url) }))
    : SIGNATURE_DISHES;

  return (
    <div data-testid="home-page" className="bg-tnt-bg">
      {/* ============== HERO ============== */}
      <section
        data-testid="hero-section"
        className="relative -mt-11 sm:-mt-12 w-full overflow-hidden bg-tnt-bg pt-14 sm:pt-16 pb-6 sm:pb-8 px-5 sm:px-8"
      >
        {/* Constrained collage card — side padding + max-width align it visually with the sections below */}
        <div
          className="relative mx-auto rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5"
          style={{
            width: "min(100%, calc((100svh - 100px) * 864 / 1216))",
            maxWidth: "min(100%, 460px)",
            aspectRatio: "864 / 1216",
          }}
        >
          <img
            data-testid="hero-collage"
            src="/images/hero-collage-v5.webp"
            alt="Taps & Tunes — live music, comedy, karaoke and great food"
            width="864"
            height="1216"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />

          {/* Buttons sit above the bottom edge of the collage, not flush with it */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25, ease: "easeOut" }}
            className="absolute inset-x-0 bottom-[6%] flex flex-col items-center justify-center gap-2.5 px-4"
          >
            {!isHidden("hero_btn1_hidden") && (
              <Link
                to={t("hero_btn1_url", "/events")}
                data-testid="hero-cta-events"
                className="btn-gold w-full max-w-xs justify-center !text-sm !py-2.5"
              >
                {t("hero_btn1_text", "See This Week's Lineup")}{" "}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            )}
            {!isHidden("hero_btn2_hidden") && (
              <Link
                to={t("hero_btn2_url", "/menu")}
                data-testid="hero-cta-menu"
                className="inline-flex w-full max-w-xs items-center justify-center font-display tracking-widest uppercase text-sm border border-white/70 text-white px-7 py-2.5 transition-all duration-300 hover:bg-white hover:text-black backdrop-blur-md bg-white/10"
              >
                {t("hero_btn2_text", "View Menu")}
              </Link>
            )}
          </motion.div>
        </div>
      </section>

      {/* ============== WEEKLY EVENTS — visual photo card gallery ============== */}
      <section
        data-testid="weekly-events-section"
        className="relative pt-8 pb-10 sm:pt-14 sm:pb-16 px-5 sm:px-8 bg-tnt-bg"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2
              data-testid="weekly-events-heading"
              className="font-display text-4xl sm:text-6xl lg:text-7xl text-tnt-ink tracking-tight leading-[1.02]"
              dangerouslySetInnerHTML={{
                __html: settings.weekly_heading
                  ? settings.weekly_heading
                  : 'THIS WEEK AT <span class="gold-text">TNT</span>',
              }}
            />
            <p className="mt-5 text-tnt-ink/75 text-base sm:text-lg max-w-xl mx-auto">
              {t("weekly_subheading", "Something happening every night.")}
            </p>
          </div>

          <div
            data-testid="weekly-event-cards"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
          >
            {weeklyEvents.map((e, i) => (
              <motion.article
                key={e.day}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                data-testid={`weekly-event-${e.day
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
                className="group relative overflow-hidden rounded-2xl aspect-[5/6] sm:aspect-[4/5] bg-black shadow-lg hover:shadow-2xl transition-shadow duration-300"
              >
                <img
                  src={e.img}
                  alt={`${e.title} at Taps & Tunes`}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* Bottom-anchored dark gradient for text readability */}
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.82) 22%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,0.1) 75%, rgba(0,0,0,0.35) 100%)",
                  }}
                />

                {/* Time pill in top-right */}
                {e.time && (
                  <div className="absolute top-4 right-4 bg-tnt-gold text-black font-display tracking-widest text-xs uppercase px-3 py-1.5 rounded-full shadow-md">
                    {e.time}
                  </div>
                )}

                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                  <p className="font-display text-tnt-gold text-[10px] sm:text-xs tracking-[0.32em] uppercase mb-2">
                    {e.day}
                  </p>
                  <h3 className="font-display text-white text-3xl sm:text-4xl tracking-tight leading-[1.05]">
                    {e.title}
                  </h3>
                  {e.desc && (
                    <p className="mt-2 text-white/85 text-sm sm:text-base leading-snug">
                      {e.desc}
                    </p>
                  )}
                </div>
              </motion.article>
            ))}
          </div>

          <div className="mt-6 sm:mt-8 text-center">
            <Link
              to="/events"
              data-testid="weekly-events-cta"
              className="inline-flex items-center font-display tracking-widest uppercase text-sm text-tnt-ink hover:text-tnt-gold transition-colors"
            >
              See Featured Events <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============== FOOD PREVIEW (light section) ============== */}
      <section
        data-testid="food-preview-section"
        className="relative py-10 sm:py-16 px-4 sm:px-6 lg:px-8 bg-tnt-surface"
      >
        <div className="mx-auto max-w-6xl">
          <div className="text-center mb-7 sm:mb-10">
            <SectionLabel
              icon={<UtensilsCrossed className="w-4 h-4" />}
              centered
            >
              From the kitchen
            </SectionLabel>
            <h2
              className="font-display text-4xl sm:text-6xl text-tnt-ink tracking-tight"
              dangerouslySetInnerHTML={{
                __html: settings.food_heading
                  ? settings.food_heading
                  : 'EAT, DRINK & <span class="gold-text">STAY LATE</span>',
              }}
            />
            <p className="mt-4 text-tnt-ink/70 max-w-2xl mx-auto text-sm sm:text-base">
              {t("food_subheading", "Full kitchen open until close — perfect for late-night cravings between sets.")}
            </p>
          </div>

          <div
            data-testid="signature-dishes"
            className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6"
          >
            {signatureDishes.map((d, i) => (
              <motion.div
                key={d.name}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                data-testid={`signature-dish-${d.name
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
                className="bg-tnt-surface2 border border-tnt-border overflow-hidden group"
              >
                <div className="aspect-[4/3] overflow-hidden bg-tnt-bg">
                  <img
                    src={d.img}
                    alt={d.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-5 sm:p-6">
                  <h3 className="font-display text-2xl sm:text-3xl text-tnt-ink tracking-tight">
                    {d.name}
                  </h3>
                  <p className="mt-2 text-tnt-ink/70 text-sm">{d.blurb}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-7 sm:mt-8 text-center">
            <Link
              to="/menu"
              data-testid="food-preview-cta"
              className="btn-gold"
            >
              View Full Menu <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============== SPECIAL GUESTS ============== */}
      <section
        data-testid="featured-guests-section"
        className="relative py-10 sm:py-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-tnt-bg"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(ellipse 70% 40% at 50% 0%, rgba(251,191,36,0.10) 0%, transparent 70%)",
          }}
        />

        <div className="relative mx-auto max-w-6xl">
          <div className="text-center mb-8 sm:mb-12 max-w-3xl mx-auto">
            <SectionLabel icon={<Mic2 className="w-4 h-4" />} centered>
              Notable Appearances
            </SectionLabel>
            <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl text-tnt-ink tracking-tight leading-[1.02]">
              FEATURED <span className="gold-text">PERFORMERS</span>
            </h2>
            <p className="mt-6 text-tnt-ink/70 text-base sm:text-lg leading-relaxed">
              National touring comedians, podcasters and musicians have all
              taken our stage. Here&apos;s a glimpse of who&apos;s passed
              through.
            </p>
          </div>

          <GuestCarousel />
        </div>
      </section>

      {/* ============== HOST YOUR EVENT (focused, no collage) ============== */}
      <section
        data-testid="host-event-section"
        className="relative py-10 sm:py-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-tnt-surface"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(251,191,36,0.10) 0%, transparent 70%)",
          }}
        />

        <div className="relative mx-auto max-w-3xl text-center">
          <SectionLabel icon={<PartyPopper className="w-4 h-4" />} centered>
            Book the venue
          </SectionLabel>
          <h2
            className="font-display text-tnt-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
            dangerouslySetInnerHTML={{
              __html: settings.host_heading
                ? settings.host_heading
                : 'HOST YOUR NEXT EVENT<br /><span class="gold-text">AT TAPS &amp; TUNES</span>',
            }}
          />
          <p className="mt-5 text-tnt-ink/75 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
            {t("host_subheading", "From birthdays and bachelorette parties to fundraisers and corporate nights — make Taps & Tunes the backdrop for your unforgettable evening.")}
          </p>

          <ul
            data-testid="event-types-list"
            className="mt-6 sm:mt-7 flex flex-wrap justify-center gap-2.5"
          >
            {EVENT_TYPES.map((t) => (
              <li
                key={t}
                className="px-4 py-2 border border-tnt-ink/25 text-tnt-ink font-display tracking-widest text-xs uppercase hover:bg-tnt-gold hover:border-tnt-gold hover:text-black transition-colors rounded-full"
              >
                {t}
              </li>
            ))}
          </ul>

          <div className="mt-6 sm:mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={`tel:${CONTACT.phoneTel}`}
              data-testid="host-event-call-cta"
              className="btn-gold"
            >
              <Phone className="w-4 h-4 mr-2" /> Call {CONTACT.phone}
            </a>
            <span className="text-tnt-ink/55 text-sm">
              to book your event
            </span>
          </div>
        </div>
      </section>

      {/* ============== CONTACT / HOURS ============== */}
      <section
        data-testid="hours-contact-section"
        className="relative py-10 sm:py-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-tnt-bg"
      >
        <div className="relative mx-auto max-w-7xl">
          {/* Exterior building photo — night shot of the venue */}
          <figure
            data-testid="exterior-photo"
            className="relative overflow-hidden rounded-2xl shadow-xl mb-5 lg:mb-8"
          >
            <img
              src="/images/exterior-night.webp"
              alt="Taps & Tunes Bar & Grill exterior at 18 Huron St, Collingwood"
              width="1600"
              height="1572"
              loading="lazy"
              decoding="async"
              className="block w-full h-auto aspect-[16/9] sm:aspect-[21/9] object-cover"
            />
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0) 60%)",
              }}
            />
            <figcaption className="absolute inset-x-0 bottom-0 p-5 sm:p-7 lg:p-9">
              <p className="font-display text-tnt-gold text-[11px] sm:text-xs tracking-[0.32em] uppercase mb-1.5">
                Find us downtown
              </p>
              <h2 className="font-display text-white text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
                18 HURON ST,
                <br className="sm:hidden" /> COLLINGWOOD
              </h2>
            </figcaption>
          </figure>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-8">
          <div data-testid="hours-card" className="card-tnt !p-5 sm:!p-7">
            <SectionLabel icon={<Clock className="w-4 h-4" />}>
              When we&apos;re open
            </SectionLabel>
            <h2 className="font-display text-4xl sm:text-5xl text-tnt-ink tracking-tight">
              HOURS OF <span className="gold-text">OPERATION</span>
            </h2>
            <div className="mt-5 sm:mt-6 space-y-3">
              <div className="flex items-baseline justify-between border-b border-tnt-border pb-3">
                <span className="font-display text-xl sm:text-2xl text-tnt-ink tracking-wide">
                  Tuesday – Sunday
                </span>
                <span className="font-display text-xl sm:text-2xl text-tnt-gold">
                  5 PM – 2 AM
                </span>
              </div>
              <div className="flex items-baseline justify-between border-b border-tnt-border pb-3">
                <span className="font-display text-xl sm:text-2xl text-tnt-ink tracking-wide">
                  Monday
                </span>
                <span className="font-display text-xl sm:text-2xl text-tnt-ink/50">
                  Closed
                </span>
              </div>
            </div>
            <p className="mt-4 text-tnt-muted leading-relaxed text-sm sm:text-base">
              Our full food menu is served from opening until closing time —
              perfect for late-night cravings between sets.
            </p>
          </div>

          <div data-testid="contact-card" className="card-tnt !p-5 sm:!p-7">
            <SectionLabel icon={<MapPin className="w-4 h-4" />}>
              Drop in & say hi
            </SectionLabel>
            <h2 className="font-display text-4xl sm:text-5xl text-tnt-ink tracking-tight">
              CONTACT <span className="gold-text">US</span>
            </h2>

            <a
              href={CONTACT.mapsUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-address"
              className="flex items-start gap-3 mt-5 sm:mt-6 group"
            >
              <MapPin className="w-6 h-6 text-tnt-gold mt-1 shrink-0" />
              <div>
                <p className="font-display text-xl sm:text-2xl text-tnt-ink tracking-wide group-hover:text-tnt-gold transition-colors">
                  {CONTACT.address}
                </p>
                <p className="text-sm text-tnt-ink/50 mt-1">
                  Tap to open in Google Maps
                </p>
              </div>
            </a>

            <a
              href={`tel:${CONTACT.phoneTel}`}
              data-testid="contact-phone"
              className="flex items-start gap-3 mt-4 group"
            >
              <Phone className="w-6 h-6 text-tnt-gold mt-1 shrink-0" />
              <div>
                <p className="font-display text-xl sm:text-2xl text-tnt-ink tracking-wide group-hover:text-tnt-gold transition-colors">
                  {CONTACT.phone}
                </p>
                <p className="text-sm text-tnt-ink/50 mt-1">
                  Call to book a table or event
                </p>
              </div>
            </a>

            <a
              href={`tel:${CONTACT.phoneTel}`}
              data-testid="contact-call-cta"
              className="btn-gold mt-5 sm:mt-6 w-full"
            >
              <Phone className="w-4 h-4 mr-2" /> Call Taps &amp; Tunes
            </a>
          </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionLabel({ icon, children, centered = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 font-display tracking-[0.3em] uppercase text-xs mb-4 ${
        light ? "text-tnt-gold" : "text-tnt-gold"
      } ${centered ? "mx-auto justify-center w-full" : ""}`}
    >
      {icon}
      <span>{children}</span>
    </div>
  );
}
